/**
 * A deliberately small syntax highlighter for marketing code samples.
 *
 * The alternative was hand-tokenising each snippet into arrays of coloured
 * segments, which made the samples unreadable and impossible to keep in sync
 * with the real API. This takes plain source and returns lines of segments.
 *
 * It is not a parser and does not try to be: these are eight-line snippets on
 * a marketing page, so the failure mode of a mis-coloured token is cosmetic.
 * Anything needing real fidelity should use a proper highlighter.
 */

/** Token classes. `CODE_COLORS` in `CodePanel` maps these to hues. */
const PLAIN = 'p';

const KEYWORDS = {
  python: ['from', 'import', 'def', 'class', 'return', 'for', 'in', 'if', 'else', 'with', 'as', 'await', 'async', 'True', 'False', 'None'],
  javascript: ['const', 'let', 'var', 'function', 'return', 'await', 'async', 'new', 'require', 'import', 'from', 'export', 'if', 'else', 'true', 'false', 'null', 'undefined'],
  bash: ['export', 'if', 'then', 'fi', 'for', 'do', 'done', 'echo', 'source'],
};

const COMMENT_PREFIX = {
  python: '#',
  bash: '#',
  javascript: '//',
};

/**
 * Ordered so earlier patterns win: strings before identifiers means a keyword
 * inside a string stays a string.
 */
function tokenPatterns(language) {
  const comment = COMMENT_PREFIX[language] || '#';
  const commentPattern = comment === '//' ? '\\/\\/[^\\n]*' : '#[^\\n]*';

  return [
    ['com', new RegExp(`^${commentPattern}`)],
    ['str', /^(?:"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/],
    ['num', /^\d+(?:\.\d+)?/],
    ['id', /^[A-Za-z_$][\w$.]*/],
    [PLAIN, /^\s+/],
    [PLAIN, /^[^\w\s"']/],
  ];
}

/**
 * A call site: an identifier immediately followed by `(`. Colouring these
 * differently is most of what makes a snippet read as code at a glance.
 */
function classifyIdentifier(text, rest, language) {
  const keywords = KEYWORDS[language] || [];
  if (keywords.includes(text)) return 'kw';
  if (rest.startsWith('(')) return 'fn';
  return PLAIN;
}

/**
 * Tokenise source into `[[{ t, c }, …], …]` — one array of segments per line.
 * Blank lines are empty arrays, which `CodePanel` renders as vertical space.
 */
export default function highlight(source, language = 'python') {
  const patterns = tokenPatterns(language);

  return source.split('\n').map((line) => {
    if (!line.trim()) return [];

    const segments = [];
    let rest = line;

    while (rest.length) {
      const match = patterns.find(([, pattern]) => pattern.test(rest));

      // Unreachable for well-formed input: the last two patterns between them
      // match any single character. Guard anyway so a surprise cannot hang.
      if (!match) {
        segments.push({ t: rest, c: PLAIN });
        break;
      }

      const [kind, pattern] = match;
      const [text] = rest.match(pattern);
      rest = rest.slice(text.length);

      const cls = kind === 'id' ? classifyIdentifier(text, rest, language) : kind;
      const previous = segments[segments.length - 1];

      // Merge runs of the same class so a line of punctuation and whitespace
      // does not become twenty single-character Text nodes.
      if (previous && previous.c === cls) previous.t += text;
      else segments.push({ t: text, c: cls });
    }

    return segments;
  });
}
